import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Coins, Gift, Trophy, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getRewards, apiRedeem } from "@/lib/api";
import { BackButton } from "@/components/BackButton";

function RewardCard({ r, user, onRedeem, busy }) {
  const affordable = user && user.points >= r.points_cost;
  const outOfStock = (r.stock ?? 0) <= 0;
  return (
    <div data-testid={`reward-card-${r.reward_id}`} className="flex flex-col bg-surfaceSecondary border border-hair rounded-2xl overflow-hidden">
      {r.image ? (
        <img src={r.image} alt={r.title} className="w-full h-40 object-cover" />
      ) : (
        <div className="w-full h-40 flex items-center justify-center bg-brandTertiary"><Gift size={40} className="text-brand" /></div>
      )}
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-serif text-lg font-semibold text-onSurface leading-snug">{r.title}</h3>
        <div className="flex items-center gap-3 mt-2 mb-4">
          <span className="inline-flex items-center gap-1 text-sm font-mono font-bold text-brand"><Coins size={15} /> {r.points_cost} Readoo</span>
          {r.value_eur ? <span className="text-xs text-subtle">valore {r.value_eur}€</span> : null}
        </div>
        <button
          data-testid={`redeem-${r.reward_id}`}
          onClick={() => onRedeem(r)}
          disabled={busy === r.reward_id || outOfStock || (user && !affordable)}
          className="mt-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {busy === r.reward_id ? <Loader2 size={15} className="animate-spin" /> : <Trophy size={15} />}
          {outOfStock ? "Esaurito" : !user ? "Accedi per riscattare" : !affordable ? "Readoo insufficienti" : "Riscatta"}
        </button>
      </div>
    </div>
  );
}

export default function Rewards() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [rewards, setRewards] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => getRewards().then((r) => setRewards(r.rewards || [])).catch(() => setRewards([])), []);
  useEffect(() => { document.title = "Premi · Readoo"; load(); }, [load]);

  const redeem = async (r) => {
    if (!user) { toast.error("Accedi per riscattare i premi"); navigate("/login"); return; }
    if (!window.confirm(`Riscattare "${r.title}" per ${r.points_cost} Readoo?`)) return;
    setBusy(r.reward_id);
    try {
      const res = await apiRedeem(r.reward_id);
      if (res.user) setUser(res.user);
      toast.success("Premio riscattato! Lo trovi in \"I miei riscatti\".");
      await load();
    } catch (e) { toast.error(e.message || "Riscatto non riuscito"); }
    finally { setBusy(""); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="rewards-page">
      <BackButton />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
        <div>
          <div className="flex items-center gap-2.5">
            <Trophy className="text-brand" size={26} />
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-onSurface">Catalogo premi</h1>
          </div>
          <p className="text-sm text-onSurfaceSecondary mt-2">Trasforma i tuoi Readoo in premi reali. Leggi articoli per accumularne di più.</p>
        </div>
        {user && (
          <span data-testid="rewards-user-points" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-surfaceSecondary border border-hair text-sm font-mono font-semibold text-onSurface shrink-0">
            <Coins size={15} className="text-brand" /> {user.points || 0} Readoo
          </span>
        )}
      </div>

      {rewards === null ? (
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>
      ) : rewards.length === 0 ? (
        <div className="py-20 text-center">
          <Gift size={40} className="text-subtle mx-auto mb-3" />
          <p className="text-onSurfaceSecondary">Nessun premio disponibile al momento. Torna presto!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rewards.map((r) => <RewardCard key={r.reward_id} r={r} user={user} onRedeem={redeem} busy={busy} />)}
        </div>
      )}
    </div>
  );
}
